import type {
  Block, Cell, ChipCell, Column, KvBlock, KvItem, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, format, isCurrencyCode, money } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Ledger & Reconciliation › Reconciliation runs (`lg-runs`), read from
 * `GET /v1/admin/reconciliation/runs` and `GET /v1/admin/reconciliation/runs/{id}`
 * (go-wallet#386, handler `internal/server/admin_ledger.go`).
 *
 * # A counter on this screen is a claim about whether anything was checked
 *
 * `rows_examined` and `findings` are not numbers on the wire. They are
 * `countView` — `{label, availability, value?, reason?, provenance?}` — and the
 * backend went to that trouble for one specific run: the unfinished one. A run
 * row is written at START, so `rows_compared` and `drift_count` sit at their
 * schema defaults until the reconciler comes back and fills them in. A process
 * that was killed halfway therefore reads, to anything that trusts those
 * columns, as "0 records examined, 0 findings" — which is the calmest possible
 * rendering of a crash, and indistinguishable from the cleanest possible run.
 *
 * So `ComposeRun` withholds both counters on an unfinished run and says why,
 * and this file's whole job is to not put the zero back. Nothing here
 * substitutes a zero, a blank or an unexplained dash: an unreadable counter
 * renders as unreadable and carries the backend's reason verbatim.
 *
 * # Zero findings is the good news. Zero rows examined is the alarm.
 *
 * These two look alike and mean opposite things, and the pair is where the
 * damage is. A completed run reporting no findings is what a healthy book looks
 * like most of the time, and painting it as a problem would teach operators to
 * ignore the column. A completed run reporting no findings *because it examined
 * nothing* is go-wallet#88's failure one layer in — the reconciler is wired,
 * scheduled, succeeding, and reading an empty table — and it is invisible in the
 * findings column alone.
 *
 * The consequence is that the findings chip is only allowed to be green when
 * `rows_examined` is available and above zero. "Found nothing" is good news only
 * from something that looked.
 *
 * # No timeout judgement is made here
 *
 * A run with no `completed_at` has not finished. It may be in flight and it may
 * have been killed, and this screen cannot tell the two apart: deciding needs
 * the expected cadence of the feed, which lives in the reconciler registry that
 * `lg-liveness` holds and this endpoint does not carry. So the age is shown —
 * measured from the server's own `started_at` to the server's own
 * `generated_at`, never from a browser clock — and the verdict is left to the
 * operator and to the screen that has the registry.
 *
 * # Read-only, structurally
 *
 * There is no re-run, no cancel, no clear and no "reconcile away" control here
 * and there is not going to be one. `reconciliation_runs` is an append-only
 * journal of what was checked, and every finding on it resolves through an
 * attributed compensating posting or it does not resolve (invariants 2 and 9).
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_ledger.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

export interface CountView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  /** Absent, never zero, when the count is not available. */
  readonly value?: number
  readonly provenance?: ProvenanceView
}

/**
 * Minor units and an explicit ISO code, always together (invariants 3 and 4).
 *
 * `amount_minor` is `number | string` because the backend is mid-flip. The
 * admin surface still sends a JSON number and says in its own comment that it
 * must not flip until this console reads both forms (go-wallet#411 waits on
 * #38); the customer surface already sends a string, because `JSON.parse`
 * rounds past 2^53 before any client code runs. Widening the reader first is
 * what makes that flip a non-event.
 *
 * These interfaces are declared here rather than imported from `dashboard.ts`
 * on purpose. That file's `MoneyView` types the field as `number`, which is the
 * shape being widened; importing it would make the string form a type error in
 * exactly the tests that prove we accept it, and would couple five adapters
 * landing in parallel through one another's files.
 */
export interface MoneyView {
  readonly amount_minor: number | string
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

/**
 * The cap that was applied, travelling with the rows it capped.
 *
 * `has_more` is answered by reading one row past the cap, never by counting the
 * table, and `next_cursor` is present only when there is more — a next cursor on
 * a final page is a button the operator clicks into nothing.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

export interface ReconRunView {
  readonly id: number
  readonly kind: string
  /** `succeeded | failed | unfinished`. A closed set; anything else fails closed. */
  readonly state: string

  readonly period_from: string
  readonly period_to: string

  readonly started_at: string
  /** Absent when the run never finished — in flight, or killed. */
  readonly completed_at?: string

  /** Counts, not bare numbers. See the file header. */
  readonly rows_examined: CountView
  readonly findings: CountView

  readonly error?: string
  /** Set only on runs scoped to one account; absent means the run was whole-book. */
  readonly account_id?: number
}

export interface ReconRunsResponse {
  readonly generated_at: string
  readonly page: PageView
  readonly runs: readonly ReconRunView[]
}

/**
 * One recorded drift.
 *
 * All three amounts are FIGURES rather than bare amounts, and that is the whole
 * design of the type. `reconcile.CompareBalances` leaves the side nobody
 * reported at its Go zero and carries the fact in `kind`, so serialising that
 * zero would tell an operator the institution reported 0.00 for an account it
 * never mentioned. The difference is withheld on a one-sided drift for the
 * louder version of the same reason: it is the number that gets quoted at the
 * institution as a shortfall.
 */
export interface ReconDriftView {
  readonly account_ref: string
  readonly currency: string
  readonly kind: string
  readonly ours: FigureView
  readonly theirs: FigureView
  readonly difference: FigureView
}

/**
 * One run with the findings it recorded.
 *
 * The run's own fields are inlined — Go embeds `reconRunView` anonymously, so
 * this is one flat object on the wire and not a nested `run` key.
 *
 * Note what is NOT here: a `generated_at`. The list endpoint stamps itself and
 * this one does not, so the only read time the detail carries is the one inside
 * `findings_provenance`.
 */
export interface ReconRunDetailResponse extends ReconRunView {
  readonly findings_availability: string
  readonly findings_reason?: string
  /** Absent, not empty, when the findings could not be read. */
  readonly findings_detail?: readonly ReconDriftView[]
  readonly findings_provenance?: ProvenanceView
}

export const RECON_RUNS_PATH = '/reconciliation/runs'

/** The detail read, addressed by run id. */
export function reconRunPath(runId: number | string): string {
  return `${RECON_RUNS_PATH}/${encodeURIComponent(String(runId))}`
}

const SOURCE = 'postgres · reconciliation_runs (GET /v1/admin/reconciliation/runs)'
const DETAIL_SOURCE = 'postgres · reconciliation_runs (GET /v1/admin/reconciliation/runs/{id})'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `availabilityOf` takes on the dashboard: an unrecognised
 * value from the backend resolves to the reading that shows the operator less
 * confidence, never more. A fourth state added upstream and defaulted to
 * "available" here would put an unverified counter on a reconciliation screen.
 */
function availabilityOf(raw: unknown): Availability {
  return typeof raw === 'string' && AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

/** The backend's own words where it sent them, and never an invented substitute. */
function reasonFor(raw: unknown, reason: unknown, fallback: string): string {
  if (typeof reason === 'string' && reason !== '') return reason
  if (typeof raw !== 'string' || !AVAILABILITIES.has(raw)) {
    return `The backend reported availability ${JSON.stringify(raw)}, which this console does not know. Treated as unreadable.`
  }
  return fallback
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a value past 2^53 has
 * already lost digits by the time it reaches here. On this screen the run id is
 * what an operator types into the detail read and quotes in a ticket; an id
 * wrong in its last places opens a different run, confidently.
 */
function exactInteger(value: unknown): number | null {
  if (typeof value !== 'number') return null
  return Number.isSafeInteger(value) ? value : null
}

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER)
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER)

/** How an amount arrived, which decides whether it can be trusted at all. */
type MinorRead =
  | { readonly ok: true; readonly minor: bigint; readonly exactAsNumber: boolean }
  | { readonly ok: false; readonly reason: string }

/**
 * Minor units, read exactly, from either wire form.
 *
 * The string branch NEVER goes through `Number()` first. `Number("9007199254740993")`
 * is `9007199254740992`, so a `Number.isSafeInteger` check on the converted
 * value passes for precisely the one input that must fail — the check would
 * certify the corruption it exists to catch. `BigInt` on the digits is exact at
 * any magnitude, so a string amount is read whole and only its *rendering* is
 * constrained afterwards.
 *
 * A JSON *number* past 2^53 is a different case and is refused rather than
 * rendered: those digits were lost inside `JSON.parse`, before this console saw
 * them, and nothing here can recover them.
 */
function readMinor(value: unknown): MinorRead {
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      return {
        ok: false,
        reason:
          'the amount arrived as a JSON number beyond exact integer range, so its last digits were lost before this console saw it and it cannot be stated.',
      }
    }
    return { ok: true, minor: BigInt(value), exactAsNumber: true }
  }
  if (typeof value === 'string') {
    // The PARSE is `money.exactMinorBig`; only the reason is this function's.
    // #40 wrote four private readers of this field and they had already drifted
    // — this one trimmed whitespace, another accepted a leading `+`. The rule
    // has a trap in it (validating a string by `Number()`-ing it first accepts
    // the one value that must be refused), so it lives in one place, and the
    // richer result shape this screen needs is a wrapper around it rather than
    // a second copy of it.
    const minor = exactMinorBig(value)
    if (minor === null) {
      return {
        ok: false,
        reason: `the amount arrived as ${JSON.stringify(value)}, which is not an integer number of minor units, so it cannot be stated (invariant 3).`,
      }
    }
    return { ok: true, minor, exactAsNumber: minor <= MAX_SAFE && minor >= MIN_SAFE }
  }
  return {
    ok: false,
    reason: 'the amount was not sent as a number or a decimal string, so it cannot be stated.',
  }
}

/**
 * An RFC-3339 instant, or nothing.
 *
 * The year-1 guard matters here more than most places: `period_from`,
 * `period_to` and `started_at` are plain `time.Time` on the Go side, not
 * pointers, so an unset one serialises as `0001-01-01T00:00:00Z` and parses
 * perfectly. Left alone it would render a reconciliation window that opened two
 * thousand years ago, and it would make the elapsed-time arithmetic below
 * report a run that has been in flight for twenty centuries.
 */
function instant(value: unknown): Date | null {
  if (typeof value !== 'string' || value === '') return null
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return null
  return at.getUTCFullYear() <= 1 ? null : at
}

/**
 * A timestamp in the format the design draws them in.
 *
 * An unparseable instant comes back verbatim rather than invented — an ugly
 * stamp is recoverable, a confidently wrong one on a reconciliation window is
 * not. A zero instant comes back empty, because it is an absence wearing a date.
 */
function stamp(value: unknown): string {
  if (typeof value !== 'string' || value === '') return ''
  const at = instant(value)
  if (at !== null) return readAtStamp(at)
  return Number.isNaN(new Date(value).getTime()) ? value : ''
}

/** Compact duration. Takes its seconds; there is no clock in this file. */
function duration(seconds: number): string {
  const s = Math.max(0, Math.trunc(seconds))
  if (s < 60) return `${String(s)}s`
  if (s < 3600) return pair(Math.floor(s / 60), 'm', s % 60, 's')
  if (s < 86400) return pair(Math.floor(s / 3600), 'h', Math.floor((s % 3600) / 60), 'm')
  return pair(Math.floor(s / 86400), 'd', Math.floor((s % 86400) / 3600), 'h')
}

/** `11m 22s`, and `52m` when the remainder is nought — never `52m 0s`. */
function pair(whole: number, unit: string, rest: number, restUnit: string): string {
  return rest === 0
    ? `${String(whole)}${unit}`
    : `${String(whole)}${unit} ${String(rest)}${restUnit}`
}

/** Whole seconds between two instants, or null if the pair runs backwards. */
function secondsBetween(from: Date, to: Date): number | null {
  const ms = to.getTime() - from.getTime()
  return ms < 0 ? null : Math.round(ms / 1000)
}

const GROUPING = new Intl.NumberFormat('en-GB', { useGrouping: true })

/** Grouped digits, explicitly en-GB — the same locale `money.format` defaults to. */
function groupInt(n: number): string {
  return GROUPING.format(n)
}

/**
 * Who the figures on this screen are answerable to, derived rather than asserted.
 *
 * A hardcoded badge would keep saying the same word on the day something
 * analytics-sourced appears in the payload, which is precisely the case law 12
 * exists to make visible.
 */
function authorityOf(provenances: readonly (ProvenanceView | undefined)[]): string {
  const authorities = new Set<string>()
  for (const p of provenances) {
    if (p !== undefined && typeof p.authority === 'string' && p.authority !== '') {
      authorities.add(p.authority)
    }
  }
  if (authorities.size === 0) return 'unattributed'
  return [...authorities].sort((a, b) => a.localeCompare(b)).join(' + ')
}

// ---------------------------------------------------------------------------
// Counts
// ---------------------------------------------------------------------------

/** A counter that can be stated, or the reason it cannot. */
type CountRead =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly availability: Availability; readonly reason: string }

/**
 * One `countView`, read fail-closed.
 *
 * An absent count object is treated as unreadable rather than as zero. Neither
 * `rows_examined` nor `findings` carries `omitempty`, so a missing one is shape
 * drift — and on this screen shape drift that defaults to zero is exactly the
 * "wired but inert" reading the endpoint was built to prevent.
 */
function readCount(c: CountView | undefined, label: string): CountRead {
  if (typeof c !== 'object' || c === null) {
    return {
      ok: false,
      availability: 'unavailable',
      reason: `${label} was not sent at all. The backend always sends this counter with an availability, so this is shape drift and not a count of zero.`,
    }
  }

  const availability = availabilityOf(c.availability)
  if (availability !== 'available') {
    return {
      ok: false,
      availability,
      reason: reasonFor(c.availability, c.reason, 'Could not be read. This is not a count of zero.'),
    }
  }

  const value = exactInteger(c.value)
  if (value === null) {
    // Available with no usable value is a contradiction, and the safe side of it
    // is to say so rather than to print the half of it we trust.
    return {
      ok: false,
      availability: 'unavailable',
      reason: `${label} was reported as available and no value we can state exactly was sent. This is not a count of zero.`,
    }
  }
  return { ok: true, value }
}

// ---------------------------------------------------------------------------
// Run state
// ---------------------------------------------------------------------------

/**
 * The closed set `ledgerread.RunState` writes.
 *
 * A string on the wire rather than a pair of nullable columns precisely because
 * the derivation is where a client gets it wrong: `!error` reads as "succeeded"
 * for a run that never finished at all. So the state is read, never re-derived,
 * and one this console does not know resolves to no verdict rather than to the
 * calm one.
 */
type RunState = 'succeeded' | 'failed' | 'unfinished'

const RUN_STATES = new Set<string>(['succeeded', 'failed', 'unfinished'])

function stateOf(raw: unknown): RunState | null {
  return typeof raw === 'string' && RUN_STATES.has(raw) ? (raw as RunState) : null
}

// ---------------------------------------------------------------------------
// One run, as a row
// ---------------------------------------------------------------------------

interface RunRead {
  readonly cells: readonly Cell[]
  /** Figures that could not be stated, each with the backend's reason. */
  readonly withheld: readonly string[]
  /** Failed runs, quoted with the error the reconciler recorded. */
  readonly failures: readonly string[]
  /** Completed runs that examined nothing — go-wallet#88's failure one layer in. */
  readonly inert: readonly string[]
  /** Runs whose fields disagree with each other, so neither is stated. */
  readonly contradictions: readonly string[]
  readonly provenances: readonly (ProvenanceView | undefined)[]
}

const RUN_COLUMNS: readonly Column[] = [
  ['Run', 'm', '92px'],
  ['Kind', 'x', '150px'],
  ['Window compared (UTC)', 'm', 'minmax(210px,1.4fr)'],
  ['Started (UTC)', 'm', '152px'],
  ['Finished', 's', 'minmax(160px,1.2fr)'],
  ['Records examined', 'c', '150px'],
  ['Findings', 'c', '112px'],
  ['Outcome', 'c', '190px'],
  ['Scope', 'x', '128px'],
]

const UNREADABLE_RUN_ROW: readonly Cell[] = [
  '—', '—', '—', '—', ['unreadable row', 'the run history returned something that is not a run'],
  ['—', 'r'], ['—', 'r'], ['unreadable', 'r'], '—',
]

/**
 * The records-examined chip.
 *
 * Zero is amber, not neutral and never green. A completed run that examined no
 * records is a reconciler reading an empty table, and it looks identical to a
 * reconciler with nothing to find — which is the state that goes unlooked-at for
 * a month (go-wallet#88).
 *
 * An unreadable counter on an UNFINISHED run is amber rather than critical: the
 * backend withholds those counters by design because they are schema defaults,
 * and a run in flight having no results yet is ordinary rather than an incident.
 * On any other state a counter that would not read is a real failure and reads
 * as one. The reason travels either way.
 */
function rowsCell(rows: CountRead, state: RunState | null): ChipCell {
  if (!rows.ok) {
    if (state === 'unfinished') return ['— not yet', 'a']
    return ['—', rows.availability === 'not_built' ? 'n' : 'r']
  }
  return rows.value === 0 ? ['0 examined', 'a'] : [groupInt(rows.value), 'n']
}

/**
 * The findings chip.
 *
 * Green is reserved for the case that has actually earned it: no findings from a
 * run that examined something. Zero findings beside zero — or unreadable —
 * records examined is not good news, it is the absence of news, and it renders
 * neutral so the outcome chip beside it can carry the alarm.
 */
function findingsCell(findings: CountRead, rows: CountRead, state: RunState | null): ChipCell {
  if (!findings.ok) {
    if (state === 'unfinished') return ['— not yet', 'a']
    return ['—', findings.availability === 'not_built' ? 'n' : 'r']
  }
  if (findings.value === 0) return rows.ok && rows.value > 0 ? ['0', 'g'] : ['0', 'n']
  return [groupInt(findings.value), 'r']
}

/**
 * The outcome chip — one verdict per run, in the order the verdicts outrank
 * each other.
 *
 * "Examined nothing" is checked before "no drift found" on purpose. A run that
 * satisfies both is the inert one, and the calmer of the two readings is the
 * wrong one.
 */
function outcomeCell(
  state: RunState | null, rawState: unknown, rows: CountRead, findings: CountRead,
): ChipCell {
  if (state === null) return [`unrecognised: ${String(rawState)}`, 'r']
  if (state === 'failed') return ['failed', 'r']
  if (state === 'unfinished') return ['not finished', 'a']
  if (rows.ok && rows.value === 0) return ['succeeded · examined nothing', 'a']
  if (findings.ok && findings.value > 0) return ['drift recorded', 'r']
  if (findings.ok && findings.value === 0 && rows.ok && rows.value > 0) return ['no drift found', 'g']
  // Succeeded, and one of the counters would not read — so neither "clean" nor
  // "inert" can be said, and neither is.
  return ['succeeded · counters unread', 'a']
}

/**
 * The finished cell: when it completed and how long it took, or how long it has
 * been open.
 *
 * The age of an unfinished run is measured from the server's `started_at` to the
 * server's `generated_at`, never from a browser clock — the two would disagree
 * by the clock skew of whoever is looking, and this figure gets quoted. It is an
 * observation, not a verdict: nothing here decides that a run is stuck, because
 * that needs the feed's expected cadence and the cadence lives in the registry
 * `lg-liveness` holds.
 */
function finishedCell(
  started: Date | null, completed: Date | null, readAt: Date | null,
): readonly [string, string] {
  if (completed === null) {
    if (started === null || readAt === null) {
      return ['not finished', 'no completion was recorded; how long it has been open cannot be stated']
    }
    const open = secondsBetween(started, readAt)
    return open === null
      ? ['not finished', 'no completion was recorded']
      : ['not finished', `open ${duration(open)} as of this read — in flight or stopped, this screen cannot tell`]
  }
  if (started === null) return [stamp(completed.toISOString()), 'start not stated, so no duration']
  const took = secondsBetween(started, completed)
  return took === null
    ? [stamp(completed.toISOString()), 'completed before it started — no duration is stated']
    : [stamp(completed.toISOString()), `took ${duration(took)}`]
}

/**
 * One run, as a row.
 *
 * Takes `unknown` because `Array.isArray` says nothing about the elements. A row
 * that is not an object is still rendered — as an unreadable row — because
 * dropping it shortens a history an operator is reading to decide whether a feed
 * is alive, and tells them nothing about why it got shorter.
 */
function readRun(raw: unknown, readAt: Date | null): RunRead {
  if (typeof raw !== 'object' || raw === null) {
    return {
      cells: UNREADABLE_RUN_ROW,
      withheld: ['a row that did not arrive as a reconciliation run object'],
      failures: [], inert: [], contradictions: [], provenances: [],
    }
  }

  const run = raw as Partial<ReconRunView>
  const withheld: string[] = []
  const failures: string[] = []
  const inert: string[] = []
  const contradictions: string[] = []

  const id = exactInteger(run.id)
  const kind = typeof run.kind === 'string' && run.kind !== '' ? run.kind : ''
  const who = id === null
    ? (kind === '' ? 'an unidentified run' : `an unidentified ${kind} run`)
    : (kind === '' ? `run ${String(id)}` : `run ${String(id)} (${kind})`)

  if (id === null) {
    withheld.push(
      `${who}: the run id could not be stated exactly, so this run cannot be opened or quoted — the detail read is addressed by that id`,
    )
  }
  if (kind === '') {
    withheld.push(`${who}: no reconciler kind was sent, so which feed produced this run is not stated`)
  }

  const state = stateOf(run.state)
  if (state === null) {
    withheld.push(
      `${who}: the state ${JSON.stringify(run.state)} is not one this console knows, so no outcome is claimed for it — an unrecognised state is never read as success`,
    )
  }

  const rows = readCount(run.rows_examined, 'Records examined')
  if (!rows.ok) withheld.push(`${who} · records examined: ${rows.reason}`)
  const findings = readCount(run.findings, 'Findings')
  if (!findings.ok) withheld.push(`${who} · findings: ${findings.reason}`)

  if (state === 'succeeded' && rows.ok && rows.value === 0) {
    inert.push(who)
  }

  const error = typeof run.error === 'string' ? run.error : ''
  if (state === 'failed') {
    failures.push(error === ''
      ? `${who}: recorded as failed and carried no error text, so what broke is not stated here`
      : `${who}: ${error}`)
  } else if (error !== '') {
    // The backend derives `failed` from a non-empty error, so this pair cannot
    // both be true. A failure hiding behind a non-failing state is the one that
    // never gets looked at.
    contradictions.push(
      `${who}: the state is ${JSON.stringify(run.state)} and an error was recorded anyway (${error}); the backend derives "failed" from that field, so the two disagree and neither is stated as the outcome`,
    )
  }

  const started = instant(run.started_at)
  if (started === null) {
    withheld.push(`${who}: no readable start time, so when this run ran is not stated`)
  }
  const completed = instant(run.completed_at)
  if (state === 'unfinished' && completed !== null) {
    // `StateOf` returns unfinished only for a zero `completed_at`, so a
    // completion timestamp beside that state means the counters were withheld as
    // schema defaults for a run that did finish.
    contradictions.push(
      `${who}: the state is "unfinished" and a completion time was sent anyway; the backend derives "unfinished" from the absence of one, so this run's counters are withheld as unfinished while it claims to have completed`,
    )
  }

  const from = stamp(run.period_from)
  const to = stamp(run.period_to)
  if (from === '' || to === '') {
    withheld.push(`${who}: the window this run compared is not fully stated, so what it covered is unknown`)
  }
  const window = from === '' && to === ''
    ? 'window not stated'
    : `${from === '' ? 'not stated' : from} → ${to === '' ? 'not stated' : to}`

  // Absent means the run was not scoped to one account — the pointer is set only
  // for a non-zero id — so "whole book" is a reading of the payload rather than
  // a default filled in for a missing field.
  let scope = 'whole book'
  if (run.account_id !== undefined) {
    const accountID = exactInteger(run.account_id)
    if (accountID === null) {
      scope = 'account · id unreadable'
      withheld.push(`${who}: an account scope was sent and its id could not be stated exactly, so which account this run covered is unknown`)
    } else {
      scope = `account ${String(accountID)}`
    }
  }

  return {
    cells: [
      id === null ? '—' : String(id),
      kind === '' ? '—' : kind,
      window,
      started === null ? 'not stated' : stamp(run.started_at),
      finishedCell(started, completed, readAt),
      rowsCell(rows, state),
      findingsCell(findings, rows, state),
      outcomeCell(state, run.state, rows, findings),
      scope,
    ],
    withheld,
    failures,
    inert,
    contradictions,
    provenances: [run.rows_examined?.provenance, run.findings?.provenance],
  }
}

// ---------------------------------------------------------------------------
// The page descriptor, which is never implied
// ---------------------------------------------------------------------------

interface PageRead {
  readonly footer: string
  /** Present when rows were left behind, for the note and the provenance. */
  readonly truncated: string | null
}

/**
 * What the page said about itself.
 *
 * A capped list rendered without this reads as the whole journal — fifty runs
 * out of four thousand, with every column looking complete. So `has_more` is
 * stated in words, and the ABSENCE of it is stated too: this read sends no
 * cursor and no filter, so `has_more: false` genuinely means the whole recorded
 * history is on screen, and saying so is what makes "no runs at all" legible as
 * the alarm it is.
 */
function readPage(page: PageView | undefined, shown: number): PageRead {
  if (typeof page !== 'object' || page === null) {
    return {
      footer: `${String(shown)} run(s) shown · the server sent no page descriptor, so whether runs were left off this page is unknown`,
      truncated:
        'runs beyond this page, if there are any — the server sent no page descriptor, so this list is not known to be the whole history',
    }
  }

  const limit = exactInteger(page.limit)
  const cursor = exactInteger(page.next_cursor)
  const limitText = limit === null ? 'a limit the server did not state' : `the server's limit of ${String(limit)}`

  // Three states, not two, and the third is the one this got wrong first.
  //
  // `has_more !== true` collapses "the server said false" into "the server did
  // not say", and answers BOTH with the confident sentence below. So a `page`
  // that arrived without a boolean `has_more` — the key dropped, or `"true"`
  // as a string after a proxy or serialiser round-trip — rendered the newest N
  // runs and told the operator in words that they were the whole recorded
  // history. On this screen that is the load-bearing lie: "no failed run in
  // this list" and "nothing has reconciled since Tuesday" both stop being
  // observations about the page and become claims about the journal.
  //
  // The declared type says `boolean`, and the backend does always send one. But
  // that type is a compile-time assertion over a payload nothing validates at
  // runtime, and this very function already defends against the whole `page`
  // object being absent — a case the same type also forbids. It stopped one
  // field short. The three sibling adapters all fail closed here; this one
  // failing open made it the odd one out.
  const more = typeof page.has_more === 'boolean' ? page.has_more : null

  if (more === null) {
    return {
      footer: `${String(shown)} run(s) shown · the page descriptor carried no usable "has_more", so whether runs were left off this page is unknown`,
      truncated:
        'runs beyond this page, if there are any — the server did not state whether more exist, so this list is not known to be the whole history',
    }
  }

  if (!more) {
    // Only sound because the read sends no `before` and no filter. If either is
    // ever added, this sentence becomes a claim about the query rather than
    // about the journal, and it has to change with it.
    return {
      footer: `${String(shown)} run(s) · the server reported no runs beyond this page, and this read sends no cursor and no filter, so this is the whole recorded run history`,
      truncated: null,
    }
  }

  return {
    footer: `${String(shown)} run(s) · TRUNCATED at ${limitText} — the server reported more runs beyond this page${cursor === null ? '' : `, continuing before run ${String(cursor)}`}`,
    truncated: `the runs older than the ${String(shown)} on this page (the server reported more beyond ${limitText}${cursor === null ? '' : `, continuing before run ${String(cursor)}`})`,
  }
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

function note(tone: Tone, kicker: string, title: string, body: string, ref: string): NoteBlock {
  return { t: 'note', tone, kicker, title, body, ref }
}

function truncatedNote(footer: string): NoteBlock {
  return note(
    'a',
    'Truncated',
    'This is not the whole run history',
    `${footer}. Everything below — how many runs failed, how many examined nothing, when a feed last produced a row — describes this page only. A run that failed before the oldest row here is not on this screen and nothing on it would say so.`,
    'page.has_more is true · the older runs are behind the cursor',
  )
}

/**
 * No runs at all — and the limit of what this screen can conclude from that.
 *
 * The corpus's empty state calls this "the never-run state, not a clean
 * history", and that is right, but only half of what has to be said. This
 * endpoint reads the run journal and nothing else: a reconciler kind that was
 * registered and never scheduled has no rows here, and so does a kind that does
 * not exist. The registry is what separates them and `lg-liveness` is the screen
 * that holds it.
 */
const EMPTY_NOTE: NoteBlock = note(
  'r',
  'No runs recorded',
  'Nothing has reconciled, and this screen cannot say what should have',
  'The run journal came back empty. That is not a clean book — it is the absence of any check having been recorded, and a reconciler that has never run has proven nothing about whether our ledger and the institution agree. This screen reads the journal only: it does not know which reconciler kinds are REGISTERED, so it cannot tell a kind that was deployed and never scheduled from one that does not exist. Reconciler liveness holds the registry and is the screen that answers that.',
  'reconciliation_runs returned no rows · the feed registry is not in this payload',
)

function failuresNote(failures: readonly string[]): NoteBlock {
  return note(
    'r',
    'Failed runs',
    `${String(failures.length)} run(s) on this page recorded a failure`,
    `${failures.join(' · ')}. A failed run's counters are real as far as it got — it examined what it examined before it broke — so the records and findings beside it are a partial check, not a complete one. Whatever it did not reach is unchecked and nothing else reports that.`,
    'reconciliation_runs.error, quoted as the reconciler recorded it',
  )
}

/**
 * The alarm the findings column cannot raise on its own.
 *
 * A run that succeeds and examines nothing is the shape of go-wallet#88 one
 * layer in: registered, scheduled, green, and reading an empty table. It reports
 * no findings, which is exactly what a healthy run reports.
 */
function inertNote(runs: readonly string[]): NoteBlock {
  return note(
    'a',
    'Succeeded and examined nothing',
    `${String(runs.length)} completed run(s) examined no records at all`,
    `${runs.join(' · ')}. Each of these ran, finished without error and compared nothing, so its report of no findings says nothing about the book. A reconciler reading an empty table is indistinguishable from one with nothing to find, and this is the only column that separates them. Check what the feed is selecting before reading its zero as agreement.`,
    'rows_examined = 0 on a run in state succeeded',
  )
}

function contradictionsNote(lines: readonly string[]): NoteBlock {
  return note(
    'r',
    'Fields that disagree',
    `${String(lines.length)} run(s) carry fields that contradict each other`,
    `${lines.join(' · ')}. The backend derives the state from these fields, so a disagreement means the derivation and the raw columns are out of step and neither can be stated as the outcome.`,
    'ledgerread.StateOf derives state from completed_at and error',
  )
}

function withheldNote(withheld: readonly string[]): NoteBlock {
  return note(
    'a',
    'Not stated',
    `${String(withheld.length)} thing(s) on this screen could not be stated`,
    withheld.join(' · '),
    'A dash is not a zero, and a withheld counter is not a run that examined nothing.',
  )
}

/**
 * What the design draws here that this endpoint does not answer for.
 *
 * The drift AMOUNT is the one that matters. The corpus draws a money column on
 * this table and the list carries a findings COUNT — so "3 findings" on this
 * screen could be three cents or three million euro, and an operator triaging by
 * this list alone has no way to tell which run to open first. The amounts exist;
 * they are on the per-run detail read, one run at a time.
 */
const NOT_SHOWN_NOTE: NoteBlock = note(
  'a',
  'Not shown',
  'What this list cannot tell you',
  'The size of a drift — the design draws a money column here and this endpoint carries a count, not an amount, so a run with three findings could be three cents or three million euro. The amounts are recorded and are on the per-run detail read, one run at a time. Also absent: which reconciler kinds are registered (a kind that has never produced a row cannot appear in a list of rows — reconciler liveness holds the registry), the cadence each kind is expected to keep, and therefore whether an unfinished run is in flight or stopped.',
  'GET /reconciliation/runs · counts only · amounts are on GET /reconciliation/runs/{id}',
)

const NO_CONTROLS_NOTE: NoteBlock = note(
  'n',
  'Read-only',
  'There is no re-run, cancel or clear control on this screen',
  'That is structural rather than unfinished. reconciliation_runs is an append-only record of what was checked and when, and a finding it recorded resolves through an attributed compensating posting or it does not resolve — there is no path in this console that nets a discrepancy away, and there is not going to be one.',
  'invariants 2 and 9 · console law: no control this backend must not expose',
)

// ---------------------------------------------------------------------------
// Composition — the list
// ---------------------------------------------------------------------------

function isRunsResponse(payload: unknown): payload is ReconRunsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<ReconRunsResponse>).runs)
}

/**
 * The two things this screen is permanently silent about.
 *
 * Both are structural rather than a bad read, so they are stated on the happiest
 * possible page, which is why this screen is `partial` and never `live`. Badging
 * it Live once the rows parse would claim it answered for everything — and the
 * drift amount, the field an operator triages on, is not in this payload at all.
 */
const STRUCTURAL_GAPS: readonly string[] = [
  'the size of each run’s drift (this endpoint carries a findings count and no amount; the figures are on the per-run detail read)',
  'reconciler kinds that have never produced a run (a list of rows cannot show the feed that never wrote one — the registry is on reconciler liveness)',
]

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, with no clock of any kind: the only "now" it uses is the server's own
 * `generated_at`, so an unfinished run's age is measured between two instants
 * the server stated and cannot drift with the reader's machine.
 */
export function composeReconRuns(r: ReconRunsResponse): ScreenData {
  const readAt = instant(r.generated_at)
  const at = stamp(r.generated_at)

  const reads = r.runs.map((run) => readRun(run, readAt))
  const page = readPage(r.page, reads.length)

  const table: TableBlock = {
    t: 'table',
    title: 'reconciliation_runs',
    // The console's source vocabulary has no 'operational' member, and 'ledger'
    // is the word it already uses for our own Postgres. What the badge is for is
    // saying this is not the analytics mirror (law 12).
    src: 'ledger',
    note: at === ''
      ? 'Newest first. Each row is one recorded comparison — what it covered, what it examined, and what it found.'
      : `Newest first, read ${at}. Each row is one recorded comparison — what it covered, what it examined, and what it found.`,
    savedView: 'Newest first',
    // No facet bar. This read sends no kind and no state filter, and a chip
    // reading `kind balance_drift` would claim a filter that is not applied —
    // which on this screen would hide every other feed's runs behind a label
    // nobody set.
    facets: false,
    cols: RUN_COLUMNS,
    rows: reads.map((x) => x.cells),
    footer: page.footer,
    foot2: 'append-only · the only action on a finding is an attributed compensating posting',
  }

  const blocks: Block[] = [table]
  if (page.truncated !== null) blocks.push(truncatedNote(page.footer))
  if (reads.length === 0) blocks.push(EMPTY_NOTE)

  const failures = reads.flatMap((x) => x.failures)
  if (failures.length > 0) blocks.push(failuresNote(failures))

  const inert = reads.flatMap((x) => x.inert)
  if (inert.length > 0) blocks.push(inertNote(inert))

  const contradictions = reads.flatMap((x) => x.contradictions)
  if (contradictions.length > 0) blocks.push(contradictionsNote(contradictions))

  const withheld = reads.flatMap((x) => x.withheld)
  if (withheld.length > 0) blocks.push(withheldNote(withheld))

  blocks.push(NOT_SHOWN_NOTE, NO_CONTROLS_NOTE)

  const missing = [...STRUCTURAL_GAPS]
  if (page.truncated !== null) missing.push(page.truncated)
  missing.push(...withheld)

  const provenance: DataSource = {
    kind: 'partial',
    source: SOURCE,
    authority: authorityOf(reads.flatMap((x) => x.provenances)),
    readAt: at === '' ? 'not stated by the server' : at,
    missing,
  }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Composition — one run's findings
// ---------------------------------------------------------------------------

/** A figure that can be rendered as an amount, or the reason it cannot. */
type Placement =
  | { readonly ok: true; readonly cell: Cell; readonly currency: string }
  | { readonly ok: false; readonly reason: string }

/**
 * Whether one drift figure may be shown, and if not, why not.
 *
 * Every refusal below is a case where the backend said `available` and the
 * figure still cannot be stated, and each one used to be capable of dropping a
 * side of a reconciliation quietly. On the screen whose numbers get quoted at
 * the institution, a missing side has to be a named absence.
 *
 * `expected` is the drift row's own currency. The figure carries a currency too,
 * and they are cross-checked rather than one being trusted: if they disagree,
 * the amount is in a currency this row does not claim, and picking either would
 * state as certain the very thing in doubt (invariant 4).
 */
function placeFigure(f: FigureView | undefined, expected: string): Placement {
  if (typeof f !== 'object' || f === null) {
    return { ok: false, reason: 'the figure was not sent at all, so this side of the comparison is unread.' }
  }

  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return {
      ok: false,
      reason: reasonFor(f.availability, f.reason, 'Could not be read. This is not a balance of zero.'),
    }
  }

  const value = f.value
  if (typeof value !== 'object' || value === null) {
    return {
      ok: false,
      reason: 'the backend reported this figure as available and sent no amount. This is not a balance of zero.',
    }
  }

  // Law 12. An analytics-sourced balance is not a balance this console may
  // render at any tone — the mirror is eventually consistent and never a source
  // of truth, and there is no freshness stamp that would make it safe to compare
  // against the institution's record.
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason: 'refused — reported as analytics-sourced, and no balance on this console may come from the analytics mirror (law 12).',
    }
  }

  if (!isCurrencyCode(value.currency)) {
    return {
      ok: false,
      reason: value.currency === ''
        ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4).'
        : `the amount arrived with ${JSON.stringify(value.currency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4).`,
    }
  }
  const currency = value.currency.trim().toUpperCase()
  if (expected !== '' && currency !== expected) {
    return {
      ok: false,
      reason: `the finding is recorded against ${expected} and this amount arrived in ${currency}; the two disagree, so neither is stated (invariant 4).`,
    }
  }

  const read = readMinor(value.amount_minor)
  if (!read.ok) return { ok: false, reason: read.reason }

  // An amount inside exact-integer range becomes a real amount cell, so the
  // renderer pairs the digits with their own ISO span. One past it is still
  // EXACT — it came off the wire as a string and was read with BigInt — but it
  // cannot be carried in the design's amount tuple, which types minor units as a
  // JS number. It renders as text through the shared formatter instead: the
  // value stays whole and the currency stays beside it.
  return read.exactAsNumber
    ? { ok: true, cell: [Number(read.minor), currency], currency }
    : { ok: true, cell: format(money(read.minor, currency)), currency }
}

const DRIFT_KINDS: Readonly<Record<string, string>> = {
  balance_amount_drift: 'both report a position, the amounts differ',
  balance_unknown_to_institution: 'we hold a position the institution does not report',
  balance_unknown_to_mirror: 'the institution reports a position we do not hold',
}

const FINDING_COLUMNS: readonly Column[] = [
  ['Account', 'm', 'minmax(170px,1.2fr)'],
  ['Disagreement', 'x', 'minmax(230px,1.6fr)'],
  ['Ours', 'a', '154px'],
  ['The institution', 'a', '154px'],
  ['Difference (ours − theirs)', 'a', '178px'],
]

interface FindingRead {
  readonly currency: string
  readonly cells: readonly Cell[]
  readonly withheld: readonly string[]
  readonly provenances: readonly (ProvenanceView | undefined)[]
}

function readFinding(raw: unknown, index: number): FindingRead {
  const position = `finding ${String(index + 1)}`
  if (typeof raw !== 'object' || raw === null) {
    return {
      currency: '',
      cells: [position, 'unreadable — this did not arrive as a finding', '—', '—', '—'],
      withheld: [`${position}: it did not arrive as a finding object, so nothing about it is stated`],
      provenances: [],
    }
  }

  const d = raw as Partial<ReconDriftView>
  const withheld: string[] = []

  const account = typeof d.account_ref === 'string' && d.account_ref !== '' ? d.account_ref : ''
  const who = account === '' ? position : `${position} (${account})`
  if (account === '') {
    withheld.push(`${who}: no account reference was sent, so which account this drift is against is not stated`)
  }

  // Grouped by the finding's own currency, and a code we cannot name puts the
  // row in its own group rather than into somebody else's. Blending an
  // unnameable amount into the EUR table is the cheapest way to break invariant 4.
  const rawCurrency = typeof d.currency === 'string' ? d.currency.trim().toUpperCase() : ''
  const currency = isCurrencyCode(rawCurrency) ? rawCurrency : ''
  if (currency === '') {
    withheld.push(
      `${who}: the finding is recorded against ${rawCurrency === '' ? 'no currency at all' : JSON.stringify(rawCurrency)}, which is not an ISO-4217 code, so its amounts cannot be stated (invariant 4)`,
    )
  }

  const rawKind = typeof d.kind === 'string' ? d.kind : ''
  const known = Object.prototype.hasOwnProperty.call(DRIFT_KINDS, rawKind)
  const disagreement = known
    ? (DRIFT_KINDS[rawKind] ?? rawKind)
    : `${rawKind === '' ? 'no kind was sent' : rawKind} — a drift kind this console does not know`
  if (!known) {
    withheld.push(`${who}: the drift kind ${JSON.stringify(rawKind)} is not one this console knows, so what the two sides disagree about is not stated in words`)
  }

  const sides: readonly (readonly [string, FigureView | undefined])[] = [
    ['ours', d.ours], ['the institution’s record', d.theirs], ['the difference', d.difference],
  ]
  const cells: Cell[] = []
  for (const [label, figure] of sides) {
    const placed = placeFigure(figure, currency)
    if (placed.ok) {
      cells.push(placed.cell)
      continue
    }
    // A dash, and never a zero. A one-sided drift genuinely has no number on the
    // missing side, and the backend withholds it precisely so nobody reads
    // "0.00" as the institution having reported nothing owed.
    cells.push('—')
    withheld.push(`${who} · ${label}: ${placed.reason}`)
  }

  return {
    currency,
    cells: [account === '' ? '—' : account, disagreement, ...cells],
    withheld,
    provenances: [d.ours?.provenance, d.theirs?.provenance, d.difference?.provenance],
  }
}

/**
 * The run itself, as a state strip.
 *
 * Each counter gets its own tile with its own chip and its own note, so an
 * unreadable one is unreadable on the tile rather than in a footnote — the same
 * reason the list gives it a chip and not a bare number.
 */
function runStrip(
  d: Partial<ReconRunDetailResponse>, readAt: Date | null, who: string,
): KvBlock {
  const state = stateOf(d.state)
  const rows = readCount(d.rows_examined, 'Records examined')
  const findings = readCount(d.findings, 'Findings')

  const started = instant(d.started_at)
  const completed = instant(d.completed_at)
  const [finished, finishedNote] = finishedCell(started, completed, readAt)

  const rowsChip = rowsCell(rows, state)
  const findingsChip = findingsCell(findings, rows, state)
  const outcome = outcomeCell(state, d.state, rows, findings)

  const from = stamp(d.period_from)
  const to = stamp(d.period_to)
  const error = typeof d.error === 'string' ? d.error : ''

  const items: KvItem[] = [
    ['reconciler kind', typeof d.kind === 'string' && d.kind !== '' ? d.kind : 'not stated', '', 'n', 'which feed recorded this run', ''],
    ['outcome', outcome[0], outcome[0], outcome[1], 'derived by the backend from completed_at and error, never re-derived here', ''],
    ['window compared', `${from === '' ? 'not stated' : from} → ${to === '' ? 'not stated' : to}`, '', 'n', 'the period this run compared, in UTC', 'm'],
    ['started', started === null ? 'not stated' : stamp(d.started_at), '', 'n', 'UTC', 'm'],
    ['finished', finished, '', completed === null ? 'a' : 'n', finishedNote, 'm'],
    [
      'records examined',
      rows.ok ? groupInt(rows.value) : '—',
      rowsChip[0], rowsChip[1],
      rows.ok
        ? (rows.value === 0 ? 'This run compared nothing, so it had nothing to find.' : 'what this run compared')
        : rows.reason,
      'm',
    ],
    [
      'findings',
      findings.ok ? groupInt(findings.value) : '—',
      findingsChip[0], findingsChip[1],
      findings.ok
        ? (findings.value === 0
            ? (rows.ok && rows.value > 0
                ? 'No drift recorded by a run that examined records. This is the good outcome.'
                : 'No drift recorded — but see records examined before reading that as agreement.')
            : 'each is a position where our ledger and the institution disagree')
        : findings.reason,
      'm',
    ],
    [
      'scope',
      d.account_id === undefined
        ? 'whole book'
        : (exactInteger(d.account_id) === null ? 'account · id unreadable' : `account ${String(exactInteger(d.account_id))}`),
      '', 'n',
      'a run scoped to one account compares only that account',
      'm',
    ],
  ]

  if (error !== '') {
    items.push(['recorded error', error, 'failed', 'r', 'quoted as the reconciler recorded it', 'm'])
  }

  return {
    t: 'kv',
    title: who,
    src: 'ledger',
    note: 'The run as it was recorded. Counters carry their own availability — an unfinished run’s are schema defaults, not results.',
    items,
  }
}

/**
 * The recorded detail as a list.
 *
 * Absent, not empty, when the read failed — the backend omits the key rather
 * than sending `[]`, which is the wire half of the same distinction this whole
 * file keeps. Anything that is not a list is treated as none rather than
 * iterated, and the availability above it has already said whether none means
 * "no findings" or "we could not read them".
 */
function recordedFindings(d: Partial<ReconRunDetailResponse>): readonly ReconDriftView[] {
  const detail: unknown = d.findings_detail
  return Array.isArray(detail) ? (detail as readonly ReconDriftView[]) : []
}

function findingsTable(currency: string, rows: readonly (readonly Cell[])[]): TableBlock {
  return {
    t: 'table',
    title: `Recorded findings — ${currency === '' ? 'currency not stated' : currency}`,
    src: 'ledger',
    note: currency === ''
      ? 'Findings whose currency this console cannot name. Their amounts are not stated: an amount without a currency is not a value this system may carry (invariant 4).'
      : `Every recorded disagreement in ${currency}, one row each. Per currency and never blended — a total across currencies would be meaningless and there is no block here that renders one.`,
    savedView: 'As recorded',
    facets: false,
    cols: FINDING_COLUMNS,
    rows,
    footer: `${String(rows.length)} finding(s) in ${currency === '' ? 'an unnameable currency' : currency} · recorded by the run, not recomputed here`,
    foot2: 'the only action on a finding is an attributed compensating posting',
  }
}

/**
 * A one-sided drift has no number on the missing side, and none is invented.
 *
 * Worth its own note because the dash in the Ours or The-institution column is
 * the single most misreadable mark on this screen: it is the difference between
 * "they say the balance is zero" and "they never mentioned this account".
 */
const ONE_SIDED_NOTE: NoteBlock = note(
  'a',
  'One-sided drifts',
  'A dash in a comparison column is an absence, never a zero',
  'Two of the three drift kinds have a position on one side and nothing at all on the other. The side that reported nothing has no amount, so it renders as a dash — and so does the difference, because ours minus theirs cannot be computed when one operand does not exist. The magnitude is not lost: it is the figure on the side that did report, where it reads as an unconfirmed position rather than as a shortfall to quote at the institution.',
  'balance_unknown_to_institution / balance_unknown_to_mirror · the difference is withheld deliberately',
)

function findingsUnreadableNote(reason: string): NoteBlock {
  return note(
    'r',
    'Findings',
    'The recorded findings could not be read',
    `${reason} The run's own posture above — whether it succeeded, how much it examined, how many findings it counted — was read and is shown. What is missing is the detail of those findings, so this screen cannot say which accounts disagree or by how much.`,
    'findings_availability is not "available"',
  )
}

function noFindingsNote(examined: boolean): NoteBlock {
  return examined
    ? note(
        'g',
        'Findings',
        'This run recorded no findings',
        'It compared records and every position it compared agreed. This is the good outcome, and it is a real one rather than an empty read: the counter above says how much was examined to reach it.',
        'findings_availability is "available" and the recorded detail is empty',
      )
    : note(
        'a',
        'Findings',
        'This run recorded no findings, and it is not clear it examined anything',
        'The detail read succeeded and is empty. That is only agreement if the run compared something, and the records-examined counter above does not establish that it did — a reconciler reading an empty table reports exactly this. Check what the feed selects before reading the empty list as the book agreeing.',
        'findings_availability is "available", the detail is empty, and rows_examined is zero or unread',
    )
}

/**
 * Folds one run and its findings into blocks.
 *
 * `at` is a fallback receipt and nothing more. This endpoint sends no
 * `generated_at` — the list one does and this one does not — so the read time
 * comes from `findings_provenance.read_at` where the backend supplied it, and
 * from the console's own receipt only when it did not. The receipt is labelled
 * as a receipt so nobody reads it as the server's word.
 */
export function composeReconRunDetail(d: ReconRunDetailResponse, at: Date): ScreenData {
  const id = exactInteger(d.id)
  const kind = typeof d.kind === 'string' && d.kind !== '' ? d.kind : ''
  const who = id === null
    ? (kind === '' ? 'An unidentified reconciliation run' : `An unidentified ${kind} run`)
    : `Reconciliation run ${String(id)}${kind === '' ? '' : ` · ${kind}`}`

  const serverReadAt = instant(d.findings_provenance?.read_at)
  const readStamp = serverReadAt === null
    ? `${readAtStamp(at)} (console receipt — this endpoint states no read time of its own)`
    : readAtStamp(serverReadAt)

  const withheld: string[] = []
  if (id === null) {
    withheld.push('the run id could not be stated exactly, so this run cannot be quoted or re-opened by id')
  }

  const rows = readCount(d.rows_examined, 'Records examined')
  if (!rows.ok) withheld.push(`records examined: ${rows.reason}`)
  const findings = readCount(d.findings, 'Findings')
  if (!findings.ok) withheld.push(`findings count: ${findings.reason}`)
  if (stateOf(d.state) === null) {
    withheld.push(`the state ${JSON.stringify(d.state)} is not one this console knows, so no outcome is claimed for this run`)
  }

  const blocks: Block[] = [runStrip(d, serverReadAt, who)]

  const availability = availabilityOf(d.findings_availability)
  if (availability !== 'available') {
    const reason = reasonFor(
      d.findings_availability, d.findings_reason,
      'The recorded findings could not be read. This is not an absence of findings.',
    )
    blocks.push(findingsUnreadableNote(reason))
    withheld.push(`the recorded findings: ${reason}`)
  } else {
    const detail = recordedFindings(d)
    if (detail.length === 0) {
      blocks.push(noFindingsNote(rows.ok && rows.value > 0))
    } else {
      const reads = detail.map(readFinding)
      // Grouped, never blended. One table per currency is invariant 4 holding in
      // the layout rather than only in the cells.
      const currencies = [...new Set(reads.map((x) => x.currency))].sort((a, b) => a.localeCompare(b))
      for (const currency of currencies) {
        blocks.push(findingsTable(currency, reads.filter((x) => x.currency === currency).map((x) => x.cells)))
      }
      if (reads.some((x) => x.cells.includes('—'))) blocks.push(ONE_SIDED_NOTE)
      withheld.push(...reads.flatMap((x) => x.withheld))

      // The two counts are two reads of the same fact and must agree; a detail
      // shorter than the counter means findings this screen is not showing, on
      // the screen an operator opens to see all of them.
      if (findings.ok && findings.value !== detail.length) {
        withheld.push(
          `${String(Math.abs(findings.value - detail.length))} finding(s) that one field has and the other does not — the run counted ${String(findings.value)} and the recorded detail carries ${String(detail.length)}, so this list is not known to be complete`,
        )
      }
    }
  }

  if (withheld.length > 0) blocks.push(withheldNote(withheld))
  blocks.push(NO_CONTROLS_NOTE)

  const authority = authorityOf([
    d.rows_examined?.provenance, d.findings?.provenance, d.findings_provenance,
    ...recordedFindings(d).flatMap(
      (f) => [f.ours?.provenance, f.theirs?.provenance, f.difference?.provenance],
    ),
  ])

  const provenance: DataSource = withheld.length === 0
    ? { kind: 'live', source: DETAIL_SOURCE, authority, readAt: readStamp }
    : { kind: 'partial', source: DETAIL_SOURCE, authority, readAt: readStamp, missing: withheld }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. Not an empty table — an empty run table reads as an
 * empty run history, and "nothing has reconciled" is a sentence an operator acts
 * on. And never the design corpus, whose five invented runs report a tidy book.
 *
 * The provenance is `unread`, not `partial`: nothing on the screen came from
 * anywhere, and the chip renders one word.
 */
function unreadableScreen(source: string, subject: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Reconciliation runs',
      title: 'This screen could not be read',
      body: `${reason} No run, counter, outcome or finding below is known — which is different from nothing having reconciled, and different again from every run being clean. ${subject}`,
      ref: decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

/** Turns a thrown read into the sentence the screen shows, or rethrows a cancellation. */
function describeFailure(error: unknown, subject: string): { reason: string; decisionRef: string | null } {
  if (error instanceof ApiError) {
    return {
      reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
      decisionRef: error.decisionRef,
    }
  }
  // An AbortError is a navigation away, not a failure to report. Let it
  // propagate so the caller's cancellation stays a cancellation.
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  return { reason: `${subject} could not be reached.`, decisionRef: null }
}

/**
 * Reads the run history.
 *
 * No query parameters are sent, deliberately. `kind`, `state`, `before` and
 * `limit` are all available and every one of them would make the footer's
 * sentence — "the server reported no runs beyond this page, so this is the whole
 * recorded history" — into a claim about the query instead of about the journal.
 * The server applies its own cap, which is the largest page it will give.
 */
export const loadReconRuns: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(RECON_RUNS_PATH)
  } catch (error) {
    const { reason, decisionRef } = describeFailure(error, 'The run history endpoint')
    return unreadableScreen(
      SOURCE,
      'Reconciler liveness is the other screen that would answer whether the feeds are running, and it reads a different endpoint.',
      reason,
      decisionRef,
    )
  }

  if (!isRunsResponse(payload)) {
    return unreadableScreen(
      SOURCE,
      'The payload carried no runs array, and an unknown shape is not an empty history.',
      'The run history endpoint returned a shape this console does not know.',
      null,
    )
  }
  return composeReconRuns(payload)
}

function isRunDetail(payload: unknown): payload is ReconRunDetailResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return typeof (payload as Partial<ReconRunDetailResponse>).findings_availability === 'string'
}

/**
 * Reads one run and the findings it recorded.
 *
 * A factory rather than a `ScreenLoader` constant because the endpoint is
 * addressed by a run id and the loader contract takes only the client. The list
 * screen does not call it: it holds up to a full page of runs and there is no
 * stable, principled way to choose which one's findings to expand — runs are
 * appended continuously, so any "most recent" or "worst" criterion changes
 * subject between refreshes, and one run's money figures under a list of many is
 * the reading that attributes a drift to the wrong run.
 */
export function loadReconRunDetail(runId: number | string): ScreenLoader {
  return async (api) => {
    const subject = `The detail of run ${String(runId)}`
    let payload: unknown
    try {
      payload = await api.get<unknown>(reconRunPath(runId))
    } catch (error) {
      const { reason, decisionRef } = describeFailure(error, subject)
      return unreadableScreen(
        DETAIL_SOURCE,
        'The run history list is unaffected and still shows this run’s counters; what is missing is which accounts disagree and by how much.',
        reason,
        decisionRef,
      )
    }

    if (!isRunDetail(payload)) {
      return unreadableScreen(
        DETAIL_SOURCE,
        'The payload carried no findings_availability, and an unknown shape is not an absence of findings.',
        `${subject} returned a shape this console does not know.`,
        null,
      )
    }
    return composeReconRunDetail(payload, new Date())
  }
}
